import { motion } from "framer-motion";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import logoFigure from "@/assets/logo-arzani-white.webp";
import heroImage from "@/assets/dest-dubai-new.webp";
import dubaiSecond from "@/assets/dest-dubai.webp";
import villaInterior from "@/assets/villa-interior.webp";
import yachtImage from "@/assets/yacht-pershing54.webp";
import carImage from "@/assets/car-lamborghini-urus.webp";
import lifestyleRestaurant from "@/assets/lifestyle-restaurant.webp";
import lifestyleChauffeur from "@/assets/lifestyle-chauffeur.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const WHATSAPP = "https://wa.me/message/2XEKJ5STR2IZJ1";

const services = [
  {
    title: "Residences",
    description:
      "Private residences across Dubai's most distinguished addresses — from Palm Jumeirah villas to skyline apartments in Downtown and Dubai Marina. Each residence is selected for its design, privacy, and proximity to the city's finest.",
    image: villaInterior,
  },
  {
    title: "Yachts",
    description:
      "A day on the water with the skyline as backdrop. Cruising past Palm Jumeirah, Burj Al Arab, and the coastline offers a distinct perspective on the city — privacy offshore, energy in the distance.",
    image: yachtImage,
  },
  {
    title: "Drive",
    description:
      "Performance and prestige vehicles delivered to your residence, hotel, or arrival terminal. From Sheikh Zayed Road to the desert road towards Hatta, the drive is part of the experience.",
    image: carImage,
  },
  {
    title: "Lifestyle",
    description:
      "Dining, beach clubs, wellness, private aviation, desert experiences, and everything between. Reservations and access are arranged in advance, allowing your days in Dubai to unfold without friction.",
    image: lifestyleRestaurant,
  },
];

const Dubai = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Group Dubai — Residences, Yachts, Drive & Lifestyle"
        description="Private residences, yacht charters, prestige vehicles and curated lifestyle arrangements across Dubai."
        path="/dubai"
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", serviceType: "Luxury concierge", areaServed: "Dubai, United Arab Emirates", provider: { "@type": "Organization", name: "Arzani Group" } }}
      />
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[100vh] flex flex-col items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Dubai skyline" className="w-full h-full object-cover scale-105" fetchPriority="high" />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground/50 via-foreground/20 to-foreground/60" />
          <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-background" />
        </div>
        <div className="relative z-10 text-center px-8 flex flex-col items-center">
          <motion.img
            src={logoFigure}
            alt="Arzani Group"
            className="h-20 md:h-28 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 1.8, ease }}
          />
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground tracking-wider uppercase mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1, duration: 1.8, ease }}
          >
            Arzani Group
          </motion.h1>
          <motion.p
            className="font-label text-primary-foreground/60 tracking-[0.4em] mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 1.8, ease }}
          >
            D U B A I
          </motion.p>
          <motion.p
            className="font-accent text-primary-foreground/60 text-sm md:text-base italic max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.8, duration: 1.8, ease }}
          >
            Ambition without limits,<br />arranged with quiet precision.
          </motion.p>
        </div>
        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.2, duration: 1.5, ease }}
        >
          <span className="font-label text-primary-foreground/30 text-[8px]">Scroll</span>
          <div className="w-px h-8 bg-primary-foreground/20" />
        </motion.div>
      </section>

      {/* Editorial Intro */}
      <section className="py-24 md:py-36 bg-background">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={dubaiSecond}
                alt="Dubai coastline"
                className="w-full h-[280px] md:h-[420px] object-cover"
                loading="lazy"
              />
            </motion.div>

            <motion.div
              className="lg:col-span-4 lg:col-start-9 flex flex-col justify-end"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, delay: 0.15, ease }}
            >
              <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
                The Destination
              </span>
              <p className="font-body text-foreground/70 text-xs md:text-sm leading-[1.9] mb-5">
                Dubai rewards those who arrive prepared.
              </p>
              <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9]">
                A city where the marina, the desert, and the skyline each offer a different
                rhythm. Between quiet mornings on the water and evenings above the city,
                Dubai moves quickly — and effortlessly, when every detail is already in place.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Centered Statement */}
      <section className="py-16 md:py-24 bg-background">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.p
            className="font-accent text-foreground/60 text-base md:text-lg lg:text-xl leading-[1.8] italic"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.4, ease }}
          >
            With Arzani, every part of your stay is carefully aligned — allowing you
            to experience Dubai with ease, discretion, and clarity.
          </motion.p>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="py-20 md:py-32 bg-background">
        <div className="container-luxury">
          <motion.div
            className="mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-4 text-[10px] tracking-[0.3em]">
              Our Services
            </span>
            <h2 className="font-display text-2xl md:text-4xl text-foreground tracking-[0.12em] uppercase mb-4">
              Curated Services in Dubai
            </h2>
            <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-xl">
              Residences, yachts, vehicles and lifestyle — arranged around your stay.
            </p>
          </motion.div>

          <div className="space-y-20 md:space-y-28">
            {services.map((service, i) => (
              <motion.div
                key={service.title}
                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1.2, delay: 0.05, ease }}
              >
                <div className={`${i % 2 === 0 ? "lg:col-span-7" : "lg:col-span-7 lg:col-start-6"} overflow-hidden`}>
                  <img
                    src={service.image}
                    alt={`Arzani ${service.title} Dubai`}
                    className="w-full h-[260px] md:h-[360px] object-cover"
                    loading="lazy"
                  />
                </div>
                <div className={`${i % 2 === 0 ? "lg:col-span-4 lg:col-start-9" : "lg:col-span-4 lg:col-start-1 lg:row-start-1"}`}>
                  <h3 className="font-display text-lg md:text-xl text-foreground tracking-[0.12em] uppercase mb-4">
                    Arzani {service.title}
                  </h3>
                  <p className="font-body text-foreground/50 text-xs md:text-sm leading-[1.9] max-w-sm mb-6">
                    {service.description}
                  </p>
                  <a
                    href={WHATSAPP}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-label text-foreground border-b border-foreground/20 pb-1 hover:border-foreground transition-colors duration-700 text-[10px] tracking-[0.15em] uppercase"
                  >
                    Inquire
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Book */}
      <section className="bg-background py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row">
            {[
              { title: "Submit Your Request", text: "Tell us your plans, preferences, and dates in Dubai." },
              { title: "Curated Options", text: "We present a tailored selection of residences, yachts, vehicles, and experiences." },
              { title: "Confirm & Arrange", text: "Once confirmed, every detail is secured and prepared in advance." },
              { title: "Experience Dubai", text: "Arrive and enjoy — everything is already in place." },
            ].map((item, i, arr) => (
              <motion.div
                key={item.title}
                className="flex md:flex-col flex-1 group"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: i * 0.1, ease }}
              >
                <div className="flex flex-col md:flex-row items-center mr-5 md:mr-0 md:mb-6">
                  <div className="w-5 h-5 rounded-full border border-foreground/20 flex items-center justify-center flex-shrink-0">
                    <div className="w-1.5 h-1.5 rounded-full bg-foreground/40" />
                  </div>
                  {i < arr.length - 1 && (
                    <div className="w-px md:w-full h-full md:h-px bg-foreground/10 md:flex-1 min-h-[40px] md:min-h-0" />
                  )}
                  {i === arr.length - 1 && <div className="md:flex-1" />}
                </div>
                <div className="pb-8 md:pb-0 md:pr-8">
                  <span className="font-label text-[10px] text-foreground/25 tracking-[0.2em] block mb-1.5">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="font-display text-sm text-foreground tracking-[0.15em] uppercase mb-1.5">
                    {item.title}
                  </h3>
                  <p className="font-body text-muted-foreground text-xs leading-relaxed">
                    {item.text}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Block */}
      <section className="py-20 md:py-32 bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <motion.div
              className="lg:col-span-3"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease }}
            >
              <img
                src={lifestyleChauffeur}
                alt="Dubai chauffeur service"
                className="w-full h-[180px] md:h-[240px] object-cover"
                loading="lazy"
              />
            </motion.div>
            <motion.div
              className="lg:col-span-7 lg:col-start-5"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.5, delay: 0.1, ease }}
            >
              <p className="font-accent text-primary-foreground/60 text-sm md:text-base lg:text-lg leading-[1.8] italic">
                Our role is to understand how each element connects — so your time
                in Dubai moves with the effortless rhythm you expect.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-36 bg-warm-black">
        <div className="container-luxury max-w-2xl mx-auto text-center">
          <motion.span
            className="font-label text-bronze block mb-6 text-[10px] tracking-[0.3em]"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Get in Touch
          </motion.span>
          <motion.h2
            className="font-display text-2xl md:text-4xl text-primary-foreground tracking-[0.12em] uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            Dubai, the Arzani Way
          </motion.h2>
          <motion.p
            className="font-body text-primary-foreground/50 text-xs md:text-sm leading-[2] mb-10"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 1.2, ease }}
          >
            From private residences and yacht charters to prestige vehicles and curated
            lifestyle experiences — let us arrange every detail of your stay in Dubai.
          </motion.p>
          <motion.a
            href={WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block px-10 py-3 border border-primary-foreground/30 text-primary-foreground/80 text-xs tracking-[0.2em] uppercase font-label hover:bg-primary-foreground/10 transition-colors duration-300"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 1, ease }}
          >
            Get in touch
          </motion.a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Dubai;
